import React from 'react';
import { FileX, Search, Inbox, TrendingUp, Users, FileText, Plus } from 'lucide-react';

/**
 * Empty State Component
 * Shows when no data is available with helpful guidance
 */
export default function EmptyState({
  icon: Icon,
  title,
  message,
  actionText,
  onAction,
  variant = 'default', // 'default', 'search', 'inbox', 'chart', 'collection'
}) {
  // Preset variants
  const variants = {
    scenarios: {
      icon: FileText,
      title: 'No Calculations Yet',
      message: 'Create your first pricing calculation to get started.',
      actionText: 'Create Calculation',
    },
    collections: {
      icon: Inbox,
      title: 'No Proposal Collections',
      message: 'Start a new proposal collection to organize your pricing calculations.',
      actionText: 'New Collection',
    },
    approvals: {
      icon: FileX,
      title: 'All Caught Up!',
      message: 'No proposals pending approval at the moment.',
    },
    search: {
      icon: Search,
      title: 'No Results Found',
      message: 'Try adjusting your search or filters to find what you\'re looking for.',
    },
    analytics: {
      icon: TrendingUp,
      title: 'No Data Available',
      message: 'Create and submit some proposals to see analytics and insights.',
    },
    users: {
      icon: Users,
      title: 'No Users Found',
      message: 'Invite team members to collaborate on pricing proposals.',
      actionText: 'Invite Users',
    },
  };

  const preset = variants[variant] || {};
  const finalIcon = Icon || preset.icon || FileX;
  const finalTitle = title || preset.title;
  const finalMessage = message || preset.message;
  const finalActionText = actionText || preset.actionText;

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      {/* Icon */}
      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
        {React.createElement(finalIcon, { className: 'w-8 h-8 text-slate-400' })}
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-slate-900 mb-2">
        {finalTitle}
      </h3>

      {/* Message */}
      <p className="text-slate-600 mb-6 max-w-sm">
        {finalMessage}
      </p>

      {/* Action Button */}
      {finalActionText && onAction && (
        <button
          onClick={onAction}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          {finalActionText}
        </button>
      )}
    </div>
  );
}

/**
 * Empty State for Tables
 */
export function EmptyTableState({ message = 'No data available', colSpan = 4 }) {
  return (
    <tr>
      <td colSpan={colSpan} className="py-12">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
            <Inbox className="w-6 h-6 text-slate-400" />
          </div>
          <p className="text-slate-600 text-sm">{message}</p>
        </div>
      </td>
    </tr>
  );
}
